import type {
  BuiltinAvatarId,
  DialogueAvatar,
  DialogueHudStyle,
  DialogueLine,
  DialogueScript,
  DialogueSide,
  MediaRef,
} from '../types';

export const BUILTIN_AVATAR_IDS: BuiltinAvatarId[] = [
  'avatar-1',
  'avatar-2',
  'avatar-3',
  'avatar-4',
  'avatar-5',
];

export const BUILTIN_AVATAR_LABELS: Record<BuiltinAvatarId, string> = {
  'avatar-1': '가이드 A',
  'avatar-2': '가이드 B',
  'avatar-3': '호스트',
  'avatar-4': '스태프',
  'avatar-5': '게스트',
};

export const DIALOGUE_FONT_OPTIONS: Array<{ id: DialogueHudStyle['fontFamily']; label: string; css: string }> = [
  { id: 'pretendard', label: 'Pretendard', css: '"Pretendard Variable", Pretendard, -apple-system, sans-serif' },
  { id: 'noto-sans', label: 'Noto Sans KR', css: '"Noto Sans KR", "Apple SD Gothic Neo", sans-serif' },
  { id: 'system', label: '시스템', css: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  { id: 'serif', label: '명조', css: '"Apple Myungjo", "Nanum Myeongjo", "Noto Serif KR", serif' },
];

export const DIALOGUE_SIZE_OPTIONS: Array<{ id: DialogueHudStyle['size']; label: string; width: number }> = [
  { id: 'S', label: '작게', width: 280 },
  { id: 'M', label: '보통', width: 360 },
  { id: 'L', label: '크게', width: 440 },
];

export const DIALOGUE_FONT_SIZE_MIN = 10;
export const DIALOGUE_FONT_SIZE_MAX = 28;

export const DIALOGUE_ANIMATION_OPTIONS: Array<{ id: DialogueHudStyle['animation']; label: string }> = [
  { id: 'fade-up', label: '위로 페이드' },
  { id: 'fade', label: '페이드' },
  { id: 'slide', label: '슬라이드' },
  { id: 'pop', label: '팝' },
  { id: 'none', label: '없음' },
];

export const DIALOGUE_FLOW_OPTIONS: Array<{ id: DialogueHudStyle['flowMode']; label: string; hint: string }> = [
  { id: 'subtitle', label: '자막처럼 표시', hint: '한 번에 하나의 대화창만 표시' },
  { id: 'chat', label: '채팅창처럼 표시', hint: '최신 대화가 아래에 쌓입니다' },
];

export const DIALOGUE_CHAT_MAX_OPTIONS: Array<{ id: DialogueHudStyle['chatMaxVisible']; label: string }> = [
  { id: 0, label: '제한없음' },
  { id: 3, label: '3개' },
  { id: 5, label: '5개' },
  { id: 7, label: '7개' },
];

export const DIALOGUE_CHAT_EXIT_OPTIONS: Array<{ id: DialogueHudStyle['chatExitAnimation']; label: string }> = [
  { id: 'fade', label: '페이드' },
  { id: 'slide-up', label: '위로 슬라이드' },
  { id: 'none', label: '없음' },
];

export const DIALOGUE_BUBBLE_COLORS = ['#fcfcfa', '#f8e8df', '#e4eee8', '#252b29', '#33474a', '#f3f1e8'];

/** Max grapheme/code-point count allowed per dialogue line. */
export const DIALOGUE_LINE_MAX_CHARS = 500;

export function dialogueCharCount(text: string) {
  return [...text].length;
}

export function clampDialogueText(text: string, max = DIALOGUE_LINE_MAX_CHARS) {
  const chars = [...text];
  if (chars.length <= max) return text;
  return chars.slice(0, max).join('');
}

export function clampDialogueFontSize(value: number) {
  if (!Number.isFinite(value)) return 13;
  return Math.min(DIALOGUE_FONT_SIZE_MAX, Math.max(DIALOGUE_FONT_SIZE_MIN, Math.round(value)));
}

export function defaultHudStyle(): DialogueHudStyle {
  return {
    backgroundColor: '#fcfcfa',
    textColor: '#202523',
    size: 'M',
    fontFamily: 'pretendard',
    fontSize: 13,
    showAvatar: true,
    transitionMs: 280,
    animation: 'fade-up',
    cameraMoveSpeed: 1,
    flowMode: 'subtitle',
    chatMaxVisible: 0,
    chatExitAnimation: 'fade',
  };
}

export function clampCameraMoveSpeed(value: number) {
  if (!Number.isFinite(value)) return 1;
  return Math.min(2, Math.max(0.1, Math.round(value * 10) / 10));
}

export function resolveHudStyle(style?: DialogueHudStyle | null): DialogueHudStyle {
  const merged = { ...defaultHudStyle(), ...style };
  merged.transitionMs = Math.min(2000, Math.max(0, Math.round(merged.transitionMs || 0)));
  merged.cameraMoveSpeed = clampCameraMoveSpeed(merged.cameraMoveSpeed);
  merged.fontSize = clampDialogueFontSize(merged.fontSize);
  if (!DIALOGUE_ANIMATION_OPTIONS.some((option) => option.id === merged.animation)) {
    merged.animation = 'fade-up';
  }
  if (merged.flowMode !== 'subtitle' && merged.flowMode !== 'chat') {
    merged.flowMode = 'subtitle';
  }
  if (![0, 3, 5, 7].includes(merged.chatMaxVisible)) {
    merged.chatMaxVisible = 0;
  }
  if (!DIALOGUE_CHAT_EXIT_OPTIONS.some((option) => option.id === merged.chatExitAnimation)) {
    merged.chatExitAnimation = 'fade';
  }
  return merged;
}

export function builtinAvatarSrc(id: BuiltinAvatarId) {
  return `/assets/avatars/${id}.png`;
}

export function resolveMediaUrl(ref: MediaRef | undefined): string | null {
  if (!ref) return null;
  if (ref.blobUrl) return ref.blobUrl;
  if (ref.storage === 'builtin' && ref.builtinId) return builtinAvatarSrc(ref.builtinId);
  if (ref.url) return ref.url;
  return null;
}

export function defaultDurationMs(text: string) {
  const length = [...text].length;
  return Math.min(12_000, Math.max(1_800, 800 + length * 70));
}

export function createEmptyDialogue(): DialogueScript {
  const mainId = crypto.randomUUID();
  return {
    avatars: [
      {
        id: mainId,
        name: '안내 에이전트',
        side: 'left',
        image: { storage: 'builtin', builtinId: 'avatar-1' },
      },
    ],
    lines: [
      {
        id: crypto.randomUUID(),
        speakerId: mainId,
        text: '안녕하세요. 이 공간을 소개해 드릴게요.',
        durationMs: defaultDurationMs('안녕하세요. 이 공간을 소개해 드릴게요.'),
      },
    ],
    hudStyle: defaultHudStyle(),
    mediaCues: [],
  };
}

export function ensureDialogue(script?: DialogueScript | null): DialogueScript {
  if (!script || !script.avatars?.length) return createEmptyDialogue();
  return {
    avatars: script.avatars,
    lines: script.lines ?? [],
    hudStyle: resolveHudStyle(script.hudStyle),
    mediaCues: script.mediaCues ?? [],
  };
}

export function mainAvatar(script: DialogueScript): DialogueAvatar | undefined {
  return script.avatars.find((avatar) => avatar.side === 'left') ?? script.avatars[0];
}

export function findSpeaker(script: DialogueScript, speakerId: string) {
  return script.avatars.find((avatar) => avatar.id === speakerId);
}

export function createGuestAvatar(index: number): DialogueAvatar {
  const builtinId = BUILTIN_AVATAR_IDS[Math.min(index + 1, BUILTIN_AVATAR_IDS.length - 1)];
  return {
    id: crypto.randomUUID(),
    name: BUILTIN_AVATAR_LABELS[builtinId],
    side: 'right',
    image: { storage: 'builtin', builtinId },
  };
}

export function createDialogueLine(speakerId: string, text = '', side: DialogueSide = 'left'): DialogueLine {
  const clamped = clampDialogueText(text);
  return {
    id: crypto.randomUUID(),
    speakerId,
    text: clamped,
    side,
    durationMs: defaultDurationMs(clamped || ' '),
  };
}

export function resolveLineSide(line: DialogueLine, speaker?: DialogueAvatar | null): DialogueSide {
  return line.side ?? speaker?.side ?? 'left';
}

export function patchLineText(line: DialogueLine, text: string): DialogueLine {
  const clamped = clampDialogueText(text);
  if (line.durationLocked) return { ...line, text: clamped };
  return { ...line, text: clamped, durationMs: defaultDurationMs(clamped) };
}
